import {CustomerMenuHeader} from "components/layouts/customerLayout/components/customerMenuHeader";
import {CustomerFluidSubPageLayout} from "components/layouts/customerLayout/customerFluidSubPageLayout/CustomerFluidSubPageLayout";
import React, {useEffect, useRef, useState} from "react";
import {MapContainer} from "./MapContainer.styles";
import {MenuOutlined, ArrowsAltOutlined, ShrinkOutlined} from "@ant-design/icons";
import {Row, Col, Switch, Drawer, Menu} from "antd";
import {useStore} from "hooks/useStore";
import {useObserver} from "mobx-react-lite";
import {useFetch} from "hooks/useFetch";
import useIsFocused from "hooks/useIsFocused";
import {mapCourierStatuses} from "constants/constants";
import {useTranslation} from "react-i18next";
import {SelectField, FilterRow, MobileFilterButton} from "components/dashboard/dashboardStyles";
import {FilterRowContainer, MapTools} from "./MapContainer.styles";
import {CustomerLeafletMap} from "./CustomerLeafletMap";

export const CustomerHeatMap = () => {
    const mapInstanceRef = useRef(null);
    const tileLayerRef = useRef(null);
    return useObserver(() => {
        const stores = useStore();
        const {t, i18n: i18nInstance} = useTranslation([
            "mapCourierStatuses",
            "courierMapTab",
            "pools"
        ]);
        const [poolGroups, setPoolGroups] = useState([]);
        const [companyList, setCompanyList] = useState([]);
        const [courierFilterList, setCourierFilterList] = useState([]);
        const isFocused = useIsFocused();

        const [recall, setRecall] = useState(true);
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
        const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
        const [menuDrawerVisible, setMenuDrawerVisible] = useState(false);
        const [forceUpdate, setForceUpdate] = useState(0);
        const [mapMode, setMapMode] = useState("relative");
        stores.meStore.map.options.showSubCompanies = true;
        const recallRef = useRef(recall);
        recallRef.current = recall;

        const courierNameValue =
            stores.meStore.map.filters.courier !== ""
                ? stores.meStore.map.filters.courier
                : undefined;

        useEffect(() => {
            if (isFocused) {
                let timeout = null;

                timeout = setTimeout(async () => {
                    await setRecall(!recallRef.current);
                }, 2000);

                return () => clearTimeout(timeout);
            }
        }, [recall, isFocused]);

        const selectedSubCompanyId = stores.meStore.selectedSubCompanyId;
        const selectedSubCompany = stores.meStore.subcompanies.find(
            subCompany => subCompany._id === selectedSubCompanyId
        );
        const selectedCity = selectedSubCompany?.city || "istanbul";
        useEffect(() => {
            setForceUpdate(prev => prev + 1);
        }, [selectedSubCompanyId]);

        const [, courierData] = useFetch({
            fetchFn: stores.courierStore.getCourierMapList.bind(null, {
                city: selectedCity,
                types: stores.meStore.map.filters.courierType,
                statuses:
                    stores.meStore.map.filters.courierStatus.length === 1 &&
                    stores.meStore.map.filters.courierStatus[0] === "available"
                        ? ["ready", "available"]
                        : stores.meStore.map.filters.courierStatus,
                dedicatedCompanyId: stores.meStore.map.filters.company,
                dedicatedSubCompanies: selectedSubCompanyId ? [selectedSubCompanyId] : [],
                dedicatedPoolIds: poolGroups
            }),
            waitForResponse: true,
            dependencies: [
                recall,
                selectedCity,
                forceUpdate,
                JSON.stringify(stores.meStore.map.filters.courierType),
                JSON.stringify(stores.meStore.map.filters.courierStatus),
                JSON.stringify(stores.meStore.map.filters.company),
                selectedSubCompanyId,
                JSON.stringify(poolGroups)
            ],
            condition: selectedCity && selectedCity !== "all"
        });

        const [
            subCompaniesLoading,
            subCompanies,
            setSubCompanies,
            setSubCompaniesLoading
        ] = useFetch({
            defaultDataValue: [],
            fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
                companyId: stores.meStore.map.filters.company,
                city: selectedCity,
                limit: 1000
            }),
            condition:
                (["", "pool"].indexOf(stores.meStore.map.filters.company) === -1 ||
                    stores.meStore.checkRole("isCompanyUser")) &&
                !!stores.meStore.map.filters.company,
            dependencies: [stores.meStore.map.filters.company, selectedCity],
            initialCall:
                ["", "pool"].indexOf(stores.meStore.map.filters.company) === -1 ||
                stores.meStore.checkRole("isCompanyUser")
        });

        const selectedSubCompanyFromAPI = subCompanies?.rows?.find(
            subCompany => subCompany._id === selectedSubCompanyId
        );

        const selectedCityFromAPI = selectedSubCompanyFromAPI?.city || selectedCity;

        const [
            subCompanyMarkerListLoading,
            subCompanyMarkerList,
            setSubCompanyMarkerList,
            setSubCompanyMarkerListLoading
        ] = useFetch({
            fetchFn: stores.companyStore.getSubCompanyMapList.bind(null, {
                companyId:
                    stores.meStore.map.filters.company === "pool"
                        ? ""
                        : stores.meStore.map.filters.company,
                disabled: stores.meStore.map.filters.disabled,
                city: selectedCityFromAPI
            }),
            initialCall: true,
            dependencies: [
                stores.meStore.map.options.showSubCompanies,
                stores.meStore.map.filters.company === "pool"
                    ? ""
                    : stores.meStore.map.filters.company,
                selectedCityFromAPI,
                stores.meStore.map.filters.disabled,
                forceUpdate
            ],
            condition: selectedCityFromAPI && selectedCityFromAPI !== "all"
        });

        useEffect(() => {
            const subCompanyFromMarkerList = subCompanyMarkerList?.find(
                sc => sc._id === selectedSubCompanyId
            );

            const locationSource = subCompanyFromMarkerList || selectedSubCompanyFromAPI;

            if (locationSource && locationSource.location) {
                stores.meStore.map.settings.center = {
                    lat: locationSource.location.latitude,
                    lng: locationSource.location.longitude
                };
                stores.meStore.map.settings.zoom = 13;
            }
        }, [selectedSubCompanyFromAPI, subCompanyMarkerList, selectedSubCompanyId]);

        const getCourierData = () => {
            let list = courierData?.courierStateList;

            if (stores.meStore.map.filters.courierType.length) {
                list = courierData?.courierStateList?.filter(courierState =>
                    stores?.meStore?.map?.filters?.courierType?.includes(courierState.type)
                );
            }
            if (stores?.meStore?.map?.filters?.courierStatus?.length) {
                list = list?.filter(courierState => {
                    if (courierState?.status === "busy") {
                        if (
                            stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnPickup")
                        ) {
                            return courierState?.deliveryState?.includes("PickUp");
                        } else if (
                            stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnDelivery")
                        ) {
                            return courierState?.deliveryState?.includes("Delivery");
                        } else if (
                            stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnReturn")
                        ) {
                            return courierState?.deliveryState?.includes("Return");
                        } else if (
                            stores?.meStore?.map?.filters?.courierStatus?.includes("busyOnTransfer")
                        ) {
                            return courierState?.deliveryState?.includes("Transfer");
                        } else {
                            return ["assigned", "waiting"].includes(courierState?.deliveryState);
                        }
                    } else {
                        return stores?.meStore?.map?.filters?.courierStatus?.includes(
                            courierState?.status
                        );
                    }
                });
            }
            if (stores?.meStore?.map?.filters?.company) {
                if (stores?.meStore?.map?.filters?.company === "pool") {
                    list = list?.filter(courierState => !courierState?.dedicatedCompanyId);
                } else {
                    const workType = stores?.appStore?.companyListFilter?.find(
                        x => x._id == stores?.meStore?.map?.filters?.company
                    )?.workType;

                    if (!poolGroups?.length) {
                        list = list?.filter(courierState => {
                            if (workType === "pool" && courierState?.delivery?.company?._id) {
                                return (
                                    courierState?.delivery?.company?._id ===
                                    stores?.meStore?.map?.filters?.company
                                );
                            }
                            return (
                                courierState?.delivery?.company?._id ===
                                    stores?.meStore?.map?.filters?.company ||
                                courierState?.dedicatedCompanyId ===
                                    stores?.meStore?.map?.filters?.company ||
                                (workType === "pool" ? !courierState?.dedicatedCompanyId : false)
                            );
                        });
                    }
                }
            }
            if (stores?.meStore?.checkRole("isCompanyUser")) {
                list = list?.filter(
                    courierState =>
                        !courierState?.isDummy ||
                        (courierState?.isDummy &&
                            !["available", "ready"].includes(courierState?.status))
                );
            }
            return list;
        };

        useEffect(() => {
            setCompanyList(
                stores.appStore.companyListFilter.map(val => {
                    return {companyId: val._id, companyName: val.companyName};
                })
            );
        }, [stores.appStore.companyListFilter]);

        useEffect(() => {
            const handleResize = () => {
                setIsMobile(window.innerWidth <= 768);
            };

            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }, []);

        const onSearchName = value => {
            const lowerValue = value.toLowerCase();
            if (value.length < stores.meStore.map.filters.courier.length) {
                stores.meStore.map.filters.courier = value;
            }
            if (value.length > 2) {
                stores.meStore.map.filters.courier = value;
                const list = getCourierData()?.filter(
                    courierState =>
                        courierState?.courier?.fullname.toLowerCase().includes(lowerValue) &&
                        courierState?.location?.coordinates[1] !== 0 &&
                        courierState?.location?.coordinates[0] !== 0
                );
                setCourierFilterList(list);
            }
        };

        const onBlurName = () => {
            stores.meStore.map.filters.courier = "";
            setCourierFilterList([]);
        };

        const onChangeName = value => {
            const lowerValue = value.toLowerCase();
            let tempCenter = {};
            stores.meStore.map.filters.courier = value;
            const filteredMap = courierFilterList.filter(
                courierState => courierState?.courier?.fullname.toLowerCase() === lowerValue
            );
            tempCenter.lat = filteredMap[0]?.location?.coordinates[1];
            tempCenter.lng = filteredMap[0]?.location?.coordinates[0];
            if (tempCenter.lat && tempCenter.lng) {
                stores.meStore.map.settings.center = tempCenter;
                stores.meStore.map.settings.zoom = 20;
            }
        };

        const onSelectName = value => {
            stores.meStore.map.filters.courier = value;
        };

        const toggleMapMode = () => {
            if (mapMode === "fixed") {
                setMapMode("relative");
            } else {
                setMapMode("fixed");
            }
        };

        useEffect(() => {
            window.addEventListener("keydown", handleEsc);
            return () => {
                window.removeEventListener("keydown", handleEsc);
            };
        }, [mapMode]);

        const handleEsc = event => {
            if (event.keyCode === 27 && mapMode === "fixed") {
                setMapMode("relative");
            }
        };

        useEffect(() => {
            if (mapMode === "fixed") {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "auto";
            }

            return () => {
                document.body.style.overflow = "auto";
            };
        }, [mapMode]);

        const {company: companyFilter} = stores.meStore.map.filters;
        const {companies: authCompanies, subcompanies: authSubCompanies} = stores.meStore.auth;
        const userSubCompanies = subCompanyMarkerList.filter(subcompanyMarker => {
            if (selectedSubCompanyId) {
                return subcompanyMarker._id.toString() === selectedSubCompanyId;
            }
            if (companyFilter !== "") {
                return subcompanyMarker.companyId.toString() === companyFilter;
            }
            if (!stores.meStore.checkRole("isCompanyUser")) {
                return true;
            }
            if (
                [
                    "cUser",
                    "oUser",
                    "sOperation",
                    "sChef",
                    "fieldManager",
                    "bm",
                    "agency",
                    "sy"
                ].includes(stores.meStore.role)
            ) {
                if (authSubCompanies?.length > 0)
                    return authSubCompanies.includes(subcompanyMarker._id);
                if (authCompanies?.length > 0)
                    return authCompanies.includes(subcompanyMarker.companyId);
            }
            return false;
        });

        return (
            <CustomerFluidSubPageLayout>
                <CustomerMenuHeader />

                <MapContainer
                    data-testid="map"
                    mode={mapMode}
                    style={isMobile ? {height: "calc(100vh - 400px)", width: "100%"} : {}}
                >
                    {isMobile && (
                        <>
                            <MobileFilterButton
                                icon={<MenuOutlined />}
                                onClick={() => setFilterDrawerVisible(true)}
                                data-testid="mobile-filter-button"
                                className="mobile-filter-button-customer"
                            >
                                Filtreler
                            </MobileFilterButton>
                        </>
                    )}
                    <MapTools
                        onClick={e => {
                            toggleMapMode();
                            e.currentTarget.blur();
                        }}
                        style={{
                            zIndex: 1000
                        }}
                    >
                        {mapMode === "relative" ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
                    </MapTools>

                    {!isMobile && (
                        <>
                            <FilterRowContainer isMobile={isMobile}>
                                <FilterRow
                                    gutter={[20, 20]}
                                    data-test-id="filter"
                                    className="filter-row-customer"
                                >
                                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                                        <SelectField
                                            mode="multiple"
                                            value={stores.meStore.map.filters.courierStatus}
                                            placeholder={t("fields.courierStatus", {
                                                ns: "common"
                                            })}
                                            onChange={value =>
                                                (stores.meStore.map.filters.courierStatus = value)
                                            }
                                            optionFilterProp="children"
                                            data-testid="courierStatusSelect"
                                            filterOption={(input, option) =>
                                                option.children.props.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .indexOf(input.toLowerCase()) >= 0
                                            }
                                            filterSort={(optionA, optionB) =>
                                                optionA.children.props.children.props.children
                                                    .toString()
                                                    .toLowerCase()
                                                    .localeCompare(
                                                        optionB.children.props.children.props.children
                                                            .toString()
                                                            .toLowerCase()
                                                    )
                                            }
                                        >
                                            {mapCourierStatuses.map(type => (
                                                <SelectField.Option
                                                    key={type.value}
                                                    value={type.value}
                                                    data-testid="courierStatusSelect_option"
                                                >
                                                    <Row justify="space-between">
                                                        <span>
                                                            {t(type.translation_key, {
                                                                ns: "mapCourierStatuses"
                                                            })}
                                                        </span>
                                                    </Row>
                                                </SelectField.Option>
                                            ))}
                                        </SelectField>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                                        <SelectField
                                            data-testid="courierNameSelect"
                                            showSearch
                                            onBlur={onBlurName}
                                            onSearch={e => onSearchName(e)}
                                            onChange={e => onChangeName(e)}
                                            onSelect={e => onSelectName(e)}
                                            value={courierNameValue}
                                            placeholder={t("filters.courierNamePlaceholder", {
                                                ns: "courierMapTab"
                                            })}
                                            showArrow={false}
                                            notFoundContent={null}
                                            defaultActiveFirstOption={false}
                                            filterOption={false}
                                        >
                                            {courierFilterList.map(courier => (
                                                <SelectField.Option
                                                    data-testid="courierNameSelect_option"
                                                    key={courier.courier._id}
                                                    value={courier.courier.fullname}
                                                >
                                                    {courier.courier.fullname}
                                                </SelectField.Option>
                                            ))}
                                        </SelectField>
                                    </Col>
                                </FilterRow>
                            </FilterRowContainer>
                        </>
                    )}

                    <CustomerLeafletMap
                        selectedSubCompanyFromAPI={selectedSubCompanyFromAPI}
                        selectedSubCompanyId={selectedSubCompanyId}
                        getCourierData={getCourierData}
                        subCompanyMarkerList={subCompanyMarkerList}
                        selectedCityFromAPI={selectedCityFromAPI}
                        companyList={companyList}
                        mapInstanceRef={mapInstanceRef}
                        tileLayerRef={tileLayerRef}
                    />
                </MapContainer>

                {isMobile && (
                    <>
                        <Drawer
                            title="Filtreler"
                            placement="top"
                            onClose={() => setFilterDrawerVisible(false)}
                            visible={filterDrawerVisible}
                            height="auto"
                            bodyStyle={{padding: "12px"}}
                            scrollToBottom={true}
                            getContainer={() => document.querySelector('[data-testid="map"]')}
                            style={{position: "absolute", zIndex: 1001}}
                        >
                            <FilterRow gutter={[20, 20]} data-test-id="filter">
                                <Col xs={24}>
                                    <h5>{t("fields.courierStatus", {ns: "common"})}</h5>
                                    <SelectField
                                        mode="multiple"
                                        value={stores.meStore.map.filters.courierStatus}
                                        placeholder={t("fields.courierStatus", {
                                            ns: "common"
                                        })}
                                        onChange={value =>
                                            (stores.meStore.map.filters.courierStatus = value)
                                        }
                                        optionFilterProp="children"
                                        data-testid="courierStatusSelect"
                                        filterOption={(input, option) =>
                                            option.children.props.children.props.children
                                                .toString()
                                                .toLowerCase()
                                                .indexOf(input.toLowerCase()) >= 0
                                        }
                                        filterSort={(optionA, optionB) =>
                                            optionA.children.props.children.props.children
                                                .toString()
                                                .toLowerCase()
                                                .localeCompare(
                                                    optionB.children.props.children.props.children
                                                        .toString()
                                                        .toLowerCase()
                                                )
                                        }
                                    >
                                        {mapCourierStatuses.map(type => (
                                            <SelectField.Option
                                                key={type.value}
                                                value={type.value}
                                                data-testid="courierStatusSelect_option"
                                            >
                                                <Row justify="space-between">
                                                    <span>
                                                        {t(type.translation_key, {
                                                            ns: "mapCourierStatuses"
                                                        })}
                                                    </span>
                                                </Row>
                                            </SelectField.Option>
                                        ))}
                                    </SelectField>
                                </Col>
                                <Col xs={24}>
                                    <SelectField
                                        data-testid="courierNameSelect"
                                        showSearch
                                        onBlur={onBlurName}
                                        onSearch={e => onSearchName(e)}
                                        onChange={e => onChangeName(e)}
                                        onSelect={e => onSelectName(e)}
                                        value={courierNameValue}
                                        placeholder={t("filters.courierNamePlaceholder", {
                                            ns: "courierMapTab"
                                        })}
                                        showArrow={false}
                                        notFoundContent={null}
                                        defaultActiveFirstOption={false}
                                        filterOption={false}
                                    >
                                        {courierFilterList.map(courier => (
                                            <SelectField.Option
                                                data-testid="courierNameSelect_option"
                                                key={courier.courier._id}
                                                value={courier.courier.fullname}
                                            >
                                                {courier.courier.fullname}
                                            </SelectField.Option>
                                        ))}
                                    </SelectField>
                                </Col>
                            </FilterRow>
                        </Drawer>
                        <Drawer
                            placement="left"
                            onClose={() => setMenuDrawerVisible(false)}
                            visible={menuDrawerVisible}
                            width="250px"
                            getContainer={() => document.querySelector('[data-testid="map"]')}
                            style={{position: "absolute", zIndex: 1001}}
                        ></Drawer>
                    </>
                )}
            </CustomerFluidSubPageLayout>
        );
    });
};
